import type { AdHocFilter, Segment } from "@domains/segments/shared/segmentsTypes.ts";
import { useSegmentsClient, useStarSegmentMutationOptions, useUnstarSegmentMutationOptions } from "@domains/segments/client/api/segmentsQuery.ts";
import { Button } from "@/components/ui/button.tsx";
import { GridListHeader, GridListItem, GridListSection } from "@/components/ui/grid-list.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Note } from "@/components/ui/note.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { SearchField, SearchInput } from "@/components/ui/search-field.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { PlusIcon } from "@heroicons/react/20/solid";
import { AdjustmentsHorizontalIcon, StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { MENU_ITEM_CLASS_NAME as SCOPE_SELECTOR_MENU_ITEM_CLASS_NAME, ScopeCount, TrailingCheck, type ScopeSelectorSegmentsSectionSlots } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useInfiniteCollection } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { useFeatureFlag } from "@squide/firefly";
import { useI18nextInstance } from "@squide/i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMixpanelClient, useMixpanelTrackingFunction } from "@workleap/telemetry/react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Collection, GridListLoadMoreItem } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ManageSegmentsModal } from "../components/manage-segments/ManageSegmentsModal.tsx";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

const SEARCH_DEBOUNCE_MS = 200;

const SEGMENT_PLACEHOLDER: Segment[] = [
    { id: "skeleton-1", name: "Xxxxxxxx Xxxxx", tags: [] },
    { id: "skeleton-2", name: "Xxxx Xxxxxxxxx", tags: [] },
    { id: "skeleton-3", name: "Xxxxxxx Xxxxxxxx", tags: [] }
];

interface SegmentStarButtonProps {
    segment: Segment;
}

function SegmentStarButton({ segment }: SegmentStarButtonProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ScopeSegmentsSection", { i18n: i18nextInstance });
    const track = useMixpanelTrackingFunction();
    const { mutate: star } = useMutation(useStarSegmentMutationOptions());
    const { mutate: unstar } = useMutation(useUnstarSegmentMutationOptions());

    const handlePress = useCallback(() => {
        const onError = () => toast.error(t("starError"), { position: "top-right" });

        if (segment.isStarred) {
            unstar({ segmentId: segment.id }, { onError });
            void track("Segment Unstarred", { SegmentId: segment.id });
        } else {
            star({ segmentId: segment.id }, { onError });
            void track("Segment Starred", { SegmentId: segment.id });
        }
    }, [segment.id, segment.isStarred, star, unstar, t, track]);

    return (
        <Button intent="plain" size="sq-sm" onPress={handlePress} aria-label={segment.isStarred ? t("unstar", { name: segment.name }) : t("star", { name: segment.name })} className="text-muted-fg group-selected:text-primary! hover:text-fg shrink-0">
            {segment.isStarred ? <StarIconSolid aria-hidden="true" className="size-4 text-current" /> : <StarIcon aria-hidden="true" className="size-4 text-current" />}
        </Button>
    );
}

interface SegmentNameProps {
    name: string;
    isLoading: boolean;
}

function SegmentName({ name, isLoading }: SegmentNameProps) {
    return (
        <Skeleton isLoading={isLoading} inert={isLoading} className="min-w-0 flex-1">
            <div>
                <div className="truncate">
                    <span>{name}</span>
                </div>
            </div>
        </Skeleton>
    );
}

export interface UseScopeSegmentsOptions {
    segmentId: string | null;
    adHocFilter: AdHocFilter | null;
    onAddFilter?: () => void;
}

export interface ScopeSegments {
    isEnabled: boolean;
    activeSegmentName: string | undefined;
    render: (context: { onRequestClose: () => void }) => ScopeSelectorSegmentsSectionSlots;
    onPopoverOpenChange: (isOpen: boolean) => void;
    persistentChrome: ReactNode;
}

export function useScopeSegments({ segmentId, adHocFilter, onAddFilter }: UseScopeSegmentsOptions): ScopeSegments {
    const isEnabled = useFeatureFlag("enable-team-segments", false);
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ScopeSegmentsSection", { i18n: i18nextInstance });
    const session = useSession();
    const { queryUtils: segmentsQueryUtils } = useSegmentsClient();
    const organizationId = session?.organizationId ?? "";
    const [manageSegmentsPanel, setManageSegmentsPanel] = useState<string | null>(null);
    const mixpanelClient = useMixpanelClient({ throwOnUndefined: false });
    const track = useMemo(() => mixpanelClient?.createTrackingFunction(), [mixpanelClient]);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
    const trimmedSearchTerm = debouncedSearchTerm.trim();

    const fetchSegmentsPage = useCallback(
        ({ continuationToken }: { continuationToken: string | undefined }) => (isEnabled && organizationId ? segmentsQueryUtils.listVisibleSegments.call({ orgId: organizationId, q: trimmedSearchTerm || undefined, continuationToken }) : Promise.resolve({ items: [], totalCount: 0 })),
        [isEnabled, organizationId, segmentsQueryUtils, trimmedSearchTerm]
    );
    const getLoadedMoreMessage = useCallback((count: number) => t("liveRegion.loadedMore", { count }), [t]);
    const getRefreshedMessage = useCallback((count: number) => t("liveRegion.results", { count }), [t]);

    const {
        items: segmentItems,
        hasContent: hasSegmentsContent,
        isLoading: isSegmentsLoading,
        isEmpty: isSegmentsEmpty,
        isColdError: isSegmentsError,
        hasActiveFilters: hasActiveSegmentsFilter,
        hasNextPage: hasMoreSegments,
        isFetchingNextPage: isFetchingMoreSegments,
        loadMore: loadMoreSegments,
        refetch: refetchSegments,
        loadedAnnouncement: segmentsLoadedAnnouncement,
        refreshedAnnouncement: segmentsRefreshedAnnouncement
    } = useInfiniteCollection<Segment>({
        queryKey: [...segmentsQueryUtils.listVisibleSegments.key(), { orgId: organizationId, q: trimmedSearchTerm }],
        queryFn: continuationToken => fetchSegmentsPage({ continuationToken }),
        placeholder: SEGMENT_PLACEHOLDER,
        hasActiveFilters: trimmedSearchTerm !== "",
        getLoadedMoreMessage,
        getRefreshedMessage,
        onError: "inline"
    });
    const handleRetrySegments = useCallback(() => refetchSegments(), [refetchSegments]);
    const renderSegmentItem = useCallback(
        (segment: Segment) => (
            <GridListItem key={segment.id} id={segment.id} textValue={segment.name} className={SCOPE_SELECTOR_MENU_ITEM_CLASS_NAME}>
                {({ isSelected }: { isSelected: boolean }) => (
                    <span className="flex min-h-8 w-full items-center justify-between gap-2">
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            {!isSegmentsLoading && <SegmentStarButton segment={segment} />}
                            <SegmentName name={segment.name} isLoading={isSegmentsLoading} />
                        </span>
                        <span className="flex items-center gap-2">
                            <ScopeCount count={segment.memberCount ?? 0} isLoading={isSegmentsLoading} />
                            <TrailingCheck isSelected={isSelected} />
                        </span>
                    </span>
                )}
            </GridListItem>
        ),
        [isSegmentsLoading]
    );

    const selectedSegmentFromList = segmentId ? segmentItems.find(segment => segment.id === segmentId) : undefined;
    const { data: resolvedSegment, isError: isResolvedSegmentError } = useQuery(
        createQueryOptions(segmentsQueryUtils.getSegment.queryOptions({ input: { orgId: organizationId, segmentId: segmentId ?? "" } }), {
            enabled: isEnabled && !!segmentId && !selectedSegmentFromList && !!organizationId,
            throwOnError: false
        })
    );
    const activeSegment = selectedSegmentFromList ?? resolvedSegment;
    const isSegmentActive = isEnabled && !!segmentId && !!activeSegment && !isResolvedSegmentError;

    const { data: activeSegmentDetail } = useQuery(
        createQueryOptions(segmentsQueryUtils.getSegment.queryOptions({ input: { orgId: organizationId, segmentId: segmentId ?? "" } }), {
            enabled: isEnabled && !!segmentId && !!organizationId,
            throwOnError: false
        })
    );
    const isHandPickedSegmentActive = !!segmentId && !!activeSegmentDetail && !activeSegmentDetail.isRuleBased;

    const handleManageSegments = useCallback(
        (onRequestClose: () => void) => {
            onRequestClose();
            setManageSegmentsPanel(adHocFilter ? "current-filters" : isSegmentActive && segmentId ? segmentId : "new");
            void track?.("Manage Segments Opened", { Source: "Team scope selector" });
        },
        [adHocFilter, isSegmentActive, segmentId, track]
    );

    const handleAddFilter = useCallback(
        (onRequestClose: () => void) => {
            onRequestClose();
            onAddFilter?.();
        },
        [onAddFilter]
    );

    const handlePopoverOpenChange = useCallback((isOpen: boolean) => {
        if (!isOpen) {
            setSearchTerm("");
        }
    }, []);

    const showSegmentsSection = isEnabled && !!organizationId && !isSegmentsError && (hasSegmentsContent || isSegmentsEmpty);
    const segmentsEmptyMessage = hasActiveSegmentsFilter ? t("noResults") : t("empty");

    const render = useCallback(
        ({ onRequestClose }: { onRequestClose: () => void }): ScopeSelectorSegmentsSectionSlots => ({
            search: (
                <div className="p-2 pb-0">
                    <SearchField aria-label={t("searchPlaceholder")} value={searchTerm} onChange={setSearchTerm}>
                        <SearchInput autoFocus placeholder={t("searchPlaceholder")} />
                    </SearchField>
                </div>
            ),
            rows: showSegmentsSection && (
                <GridListSection className="divide-y-0">
                    {/* intent-ui-deviation: its own default className (font-semibold text-sm/6) is
                        overridden here for the uppercase treatment the design calls for. */}
                    <GridListHeader className="text-muted-fg px-3 py-2 text-xs/3 font-medium tracking-wide uppercase sm:px-2.5 sm:py-1.5">{t("header")}</GridListHeader>
                    {isSegmentsEmpty ? (
                        <GridListItem id="no-segments-message" textValue={segmentsEmptyMessage} isDisabled className={SCOPE_SELECTOR_MENU_ITEM_CLASS_NAME}>
                            <span className="text-sm">{segmentsEmptyMessage}</span>
                        </GridListItem>
                    ) : (
                        <>
                            <Collection items={segmentItems} dependencies={[isSegmentsLoading]}>
                                {renderSegmentItem}
                            </Collection>
                            {hasMoreSegments && !isSegmentsLoading && (
                                <GridListLoadMoreItem onLoadMore={loadMoreSegments} isLoading={isFetchingMoreSegments}>
                                    <div className="flex items-center justify-center gap-2 py-2 text-sm">
                                        <ProgressCircle isIndeterminate aria-label={t("loadMore")} className="text-muted-fg size-4" />
                                        <span className="text-muted-fg">{t("loadMore")}</span>
                                    </div>
                                </GridListLoadMoreItem>
                            )}
                        </>
                    )}
                </GridListSection>
            ),
            footer: (
                <>
                    <output className="sr-only">{segmentsLoadedAnnouncement}</output>
                    <output className="sr-only">{segmentsRefreshedAnnouncement}</output>
                    {isSegmentsError && (
                        <div className="space-y-2 p-2">
                            <Note intent="danger" role="alert">
                                {t("error")}
                            </Note>
                            <Button intent="outline" size="sm" onPress={handleRetrySegments} className="w-full">
                                {t("retry")}
                            </Button>
                        </div>
                    )}
                    <Separator />
                    <div className="flex flex-col p-2">
                        {onAddFilter && (
                            <Button intent="plain" onPress={() => handleAddFilter(onRequestClose)} isDisabled={isHandPickedSegmentActive} className="w-full justify-start">
                                <PlusIcon aria-hidden="true" />
                                {t("addFilter")}
                            </Button>
                        )}
                        <Button intent="plain" onPress={() => handleManageSegments(onRequestClose)} className="w-full justify-start">
                            <AdjustmentsHorizontalIcon aria-hidden="true" />
                            {t("manageSegments")}
                        </Button>
                    </div>
                </>
            )
        }),
        [
            t,
            searchTerm,
            showSegmentsSection,
            isSegmentsEmpty,
            segmentsEmptyMessage,
            segmentItems,
            isSegmentsLoading,
            renderSegmentItem,
            hasMoreSegments,
            loadMoreSegments,
            isFetchingMoreSegments,
            segmentsLoadedAnnouncement,
            segmentsRefreshedAnnouncement,
            isSegmentsError,
            handleRetrySegments,
            onAddFilter,
            handleAddFilter,
            isHandPickedSegmentActive,
            handleManageSegments
        ]
    );

    const persistentChrome = useMemo(
        () => (
            <>
                <output className="sr-only">{isSegmentsLoading && t("liveRegion.loadingSegments")}</output>
                <ManageSegmentsModal panel={manageSegmentsPanel} onPanelChange={setManageSegmentsPanel} />
            </>
        ),
        [isSegmentsLoading, t, manageSegmentsPanel]
    );

    return {
        isEnabled,
        activeSegmentName: isSegmentActive ? activeSegment?.name : undefined,
        render,
        onPopoverOpenChange: handlePopoverOpenChange,
        persistentChrome
    };
}
